import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  ArrowLeft, Loader2, MapPin, Shield, Heart, 
  Briefcase, Home, Plane, DollarSign, GraduationCap, Users,
  Wifi, Calculator, Video, Phone, Building, Clock, Globe,
  FileText, ExternalLink, Star
} from "lucide-react";
import RegionalVibeCheck from "../components/report/RegionalVibeCheck";
import EnergyGridCard from "../components/report/EnergyGridCard";

const SECTION_ICONS = {
  "Executive Summary": MapPin,
  "Relocation Timeline": Clock,
  "Visa & Immigration": Plane,
  "Cost of Living": DollarSign,
  "Healthcare": Heart,
  "Healthcare Comparison": Heart,
  "Real Estate": Home,
  "Banking": Briefcase,
  "Taxes": Calculator,
  "Internet & Remote Work": Wifi,
  "Daily Life": Users,
  "Work Opportunities": Briefcase,
  "Education & Schools": GraduationCap,
  "A Day in the Life": Video,
};

const SampleReportPage = () => {
  const navigate = useNavigate();
  const { countryName } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const fetchSampleReport = async () => {
      try {
        const response = await axios.get(
          `${API}/reports/sample/${encodeURIComponent(countryName || "Portugal")}`
        );
        setReport(response.data);
      } catch (error) {
        toast.error("Failed to load sample report");
      } finally {
        setLoading(false);
      }
    };

    fetchSampleReport();
  }, [countryName]);

  const keyStatsEntries = useMemo(() => {
    if (!report?.content?.key_stats) return [];
    return Object.entries(report.content.key_stats).filter(([key]) => !key.includes('_usa'));
  }, [report]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Generating sample report...</p>
          <p className="text-sm text-muted-foreground mt-2">
            This may take up to 30 seconds...
          </p>
        </div>
      </div>
    );
  }

  if (!report?.content) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-primary mb-4">Sample Report</h2>
          <p className="text-muted-foreground mb-6">
            Unable to load the sample report. Please try again.
          </p>
          <Button onClick={() => navigate("/")} data-testid="back-home-btn">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const content = report.content;

  return (
    <div className="min-h-screen py-8 px-6 bg-background">
      <div className="noise-overlay" />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-4 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>
          
          {/* Sample Banner */}
          <div className="bg-gradient-to-r from-secondary/20 to-primary/20 border border-secondary/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <Star className="h-6 w-6 text-secondary" />
              <div>
                <p className="font-semibold text-primary">Sample Report Preview</p>
                <p className="text-sm text-muted-foreground">
                  This is an example of what you'll receive after purchasing. Full reports include even more detailed information.
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-primary mb-2" data-testid="report-title">
                {content.country_name || countryName}
              </h1>
              <p className="text-muted-foreground">
                Comprehensive Relocation Guide
              </p>
            </div>
            <Button
              onClick={() => navigate("/auth")}
              className="btn-animated bg-secondary hover:bg-secondary/90 gap-2"
              data-testid="get-started-btn"
            >
              Get Your Personalized Report
            </Button>
          </div>
        </div>

        {/* Hero Image */}
        {content.images && (
          <div className="mb-8 rounded-2xl overflow-hidden border border-primary/10">
            <img 
              src={content.images.hero}
              alt={`${content.country_name || countryName} landscape`}
              className="w-full h-64 object-cover"
              data-testid="hero-image"
            />
          </div>
        )}

        {/* Key Stats */}
        {content.key_stats && (
          <div className="mb-8">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {keyStatsEntries.map(([key, value]) => {
                // Side-by-side USA baseline for any matched _usa key
                const usaValue = content.key_stats[`${key}_usa`];

                // Get rating label based on value
                const getRating = (val, type) => {
                  if (type === 'healthcare_index') {
                    if (val >= 80) return { label: 'Excellent', color: 'text-green-600' };
                    if (val >= 70) return { label: 'Very Good', color: 'text-green-500' };
                    if (val >= 60) return { label: 'Good', color: 'text-blue-500' };
                    if (val >= 50) return { label: 'Moderate', color: 'text-yellow-500' };
                    return { label: 'Below Average', color: 'text-orange-500' };
                  }
                  if (val >= 80) return { label: 'Excellent', color: 'text-green-600' };
                  if (val >= 60) return { label: 'Good', color: 'text-green-500' };
                  if (val >= 40) return { label: 'Moderate', color: 'text-yellow-500' };
                  return { label: 'Low', color: 'text-orange-500' };
                };

                const rating = getRating(value, key);

                return (
                  <div
                    key={key}
                    className="bg-card rounded-xl border border-primary/10 p-4 text-center"
                  >
                    <p className="font-mono text-2xl font-bold text-primary">{value}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {key === 'healthcare_index' ? 'Numbeo Health Index' : key.replace(/_/g, " ")}
                    </p>
                    <p className={`text-xs mt-1 font-medium ${rating.color}`}>
                      {rating.label}
                    </p>
                    {usaValue !== undefined && (
                      <p className="text-xs text-accent mt-1">
                        vs USA: {usaValue}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Healthcare Index Explanation */}
            <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-primary/10">
              <p className="text-sm text-muted-foreground">
                <strong>Numbeo Health Care Index:</strong> Measures overall healthcare system quality based on surveys about medical staff competence, equipment, costs, and wait times. 
                Scale: 0-100 (Higher = Better). <strong>80+</strong> = Excellent, <strong>70-79</strong> = Very Good, <strong>60-69</strong> = Good, <strong>50-59</strong> = Moderate.
                USA averages ~69.
              </p>
            </div>
          </div>
        )}

        {/* Residency Requirements */}
        {content.residency_requirements && (
          <div className="bg-gradient-to-r from-secondary/10 to-primary/10 rounded-xl p-6 mb-8 border border-secondary/20">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Residency Financial Requirements
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <p className="font-mono text-2xl font-bold text-primary">
                  ${content.residency_requirements.minimum_savings_usd?.toLocaleString() || "Varies"}
                </p>
                <p className="text-sm text-muted-foreground">Minimum Savings Required</p>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <p className="font-mono text-2xl font-bold text-secondary">
                  ${content.residency_requirements.monthly_income_required_usd?.toLocaleString() || "Varies"}/mo
                </p>
                <p className="text-sm text-muted-foreground">Monthly Income Required</p>
              </div>
              <div className="p-4 bg-white/50 rounded-lg">
                <p className="text-sm text-foreground/80">
                  {content.residency_requirements.notes || "Requirements vary by visa type"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Video Links */}
        {content.video_links && content.video_links.length > 0 && (
          <div className="bg-primary/5 rounded-xl p-6 mb-8 border border-primary/10">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
              <Video className="h-5 w-5" />
              Watch: A Day in the Life
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {content.video_links.map((video, i) => (
                <a
                  key={video.url || `video-${i}`}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 bg-white/70 rounded-lg border border-primary/10 hover:border-primary/30 transition-colors"
                  data-testid={`video-link-${i}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Video className="h-6 w-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary">{video.title}</h3>
                    <p className="text-sm text-muted-foreground">{video.description}</p>
                    {video.duration && (
                      <p className="text-xs text-accent mt-1">Duration: {video.duration}</p>
                    )}
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground ml-auto flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Quick Facts */}
        {content.quick_facts && content.quick_facts.length > 0 && (
          <div className="bg-accent/10 rounded-xl p-6 mb-8 border border-accent/20">
            <h2 className="text-xl font-bold text-primary mb-4">Quick Facts</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {content.quick_facts.map((fact, i) => (
                <li key={`fact-${i}-${fact.slice(0, 20)}`} className="flex items-start gap-2">
                  <span className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                  <span className="text-foreground/80">{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Comparative Regional Analysis */}
        <RegionalVibeCheck regional={content.regional_analysis} />

        {/* Energy Grid Dependability */}
        <EnergyGridCard energy={content.energy_grid} />

        {/* Image Gallery */}
        {content.images && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="rounded-xl overflow-hidden border border-primary/10">
              <img 
                src={content.images.lifestyle}
                alt={`${content.country_name || countryName} lifestyle`}
                className="w-full h-40 object-cover hover:scale-105 transition-transform"
              />
              <p className="text-center text-sm text-muted-foreground py-2 bg-card">City Life</p>
            </div>
            <div className="rounded-xl overflow-hidden border border-primary/10">
              <img 
                src={content.images.food}
                alt={`${content.country_name || countryName} cuisine`}
                className="w-full h-40 object-cover hover:scale-105 transition-transform"
              />
              <p className="text-center text-sm text-muted-foreground py-2 bg-card">Local Cuisine</p>
            </div>
            <div className="rounded-xl overflow-hidden border border-primary/10">
              <img 
                src={content.images.nature}
                alt={`${content.country_name || countryName} nature`}
                className="w-full h-40 object-cover hover:scale-105 transition-transform"
              />
              <p className="text-center text-sm text-muted-foreground py-2 bg-card">Nature & Travel</p>
            </div>
          </div>
        )}

        {/* Table of Contents */}
        {content.sections && (
          <div className="bg-card rounded-xl border border-primary/10 p-6 mb-8">
            <h2 className="text-lg font-bold text-primary mb-4">Table of Contents</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {content.sections.map((section, index) => (
                <button
                  key={section.title || `toc-${index}`}
                  onClick={() => setActiveSection(index)}
                  className={`text-left p-2 rounded-lg text-sm transition-colors ${
                    activeSection === index 
                      ? "bg-primary/10 text-primary" 
                      : "hover:bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {index + 1}. {section.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Report Sections */}
        <div className="space-y-8">
          {content.sections?.map((section, index) => {
            // Find matching icon
            let IconComponent = MapPin;
            Object.entries(SECTION_ICONS).forEach(([key, icon]) => {
              if (section.title.toLowerCase().includes(key.toLowerCase())) {
                IconComponent = icon;
              }
            });
            
            return (
              <div 
                key={section.title || `section-${index}`}
                id={`section-${index}`}
                className="bg-card rounded-2xl border border-primary/10 p-8"
                data-testid={`section-${index}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-primary">{section.title}</h2>
                </div>
                
                <div className="prose prose-stone max-w-none">
                  <p className="text-foreground/80 whitespace-pre-line leading-relaxed">
                    {section.content}
                  </p>
                </div>
                
                {section.subsections && section.subsections.length > 0 && (
                  <div className="mt-6 space-y-4 border-t border-primary/10 pt-6">
                    {section.subsections.map((sub, subIndex) => (
                      <div key={sub.title || `sub-${subIndex}`} className="pl-4 border-l-2 border-secondary/30">
                        <h3 className="text-lg font-semibold text-primary mb-2">
                          {sub.title}
                        </h3>
                        <p className="text-foreground/70 whitespace-pre-line">
                          {sub.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Important Contacts */}
        {content.important_contacts && content.important_contacts.length > 0 && (
          <div className="mt-8 bg-primary/5 rounded-2xl border border-primary/10 p-8">
            <div className="flex items-center gap-2 mb-6">
              <Phone className="h-5 w-5 text-primary" />
              <h2 className="text-2xl font-bold text-primary">Important Contacts</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {content.important_contacts.map((contact, i) => (
                <div 
                  key={contact.name || `contact-${i}`}
                  className="bg-white/70 rounded-xl p-4 border border-primary/10"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-primary">{contact.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{contact.address}</p>
                      <p className="text-sm text-foreground mt-1">
                        <span className="font-medium">Phone:</span> {contact.phone}
                      </p>
                      {contact.website && (
                        <a 
                          href={contact.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-secondary hover:underline flex items-center gap-1 mt-1"
                        >
                          Visit Website <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 text-center border border-primary/20">
          <h2 className="text-2xl font-bold text-primary mb-3">
            Ready to Find Your Perfect Destination?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Take our comprehensive questionnaire and get personalized country recommendations 
            based on your unique lifestyle, budget, and preferences.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            className="btn-animated bg-primary hover:bg-primary/90 text-lg px-8 py-6"
            data-testid="cta-get-started"
          >
            Get Started Free
          </Button>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-muted-foreground text-sm">
          <p>Sample report generated on {new Date(report.generated_at).toLocaleDateString()}</p>
          <p className="mt-1">
            This is a sample report for demonstration purposes. 
            Actual reports are personalized based on your questionnaire responses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SampleReportPage;
